import { format, isAfter, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import type { TFunction } from 'i18next'

import type { BadgeIcon, BadgeVariant, PickerStatus } from '@percent/lemonade'
import type {
  CandidateAggregateScore,
  CandidateFormSubmissionAnswer,
  RejectionReasonDetail,
  DisbursedBy,
  ScoreScale,
  Task
} from '@percent/partner-dashboard/services/grants/grantsService.types'
import { CurrentStateLabel } from '@percent/partner-dashboard/services/grants/grantsService.types'
import { type CurrencyCode, formatMoney } from '@percent/utility'

export type StateBadge = {
  label: string
  variant: BadgeVariant
  icon?: BadgeIcon
  tooltip?: string
}

export type GetCurrentStateBadgeParams = {
  currentState: string
  rejectionReason?: string
  rejectionReasonDetail?: RejectionReasonDetail
  t: TFunction
}

export type FormatRejectionReasonParams = {
  rejectionReason?: string | null
  rejectionReasonDetail?: RejectionReasonDetail
}

const REJECTION_REASON_CODES = 'grants.candidates.rejectionReasonCodes'

/**
 * The refs we have copy for. `conditions` is an open set — a `field` or `payloadCondition` ref is a
 * path a program author chose — so an unrecognised one is dropped rather than shown as the internal
 * path it is. When that leaves nothing, the rejection falls back to the string the backend sends.
 */
const CONDITION_REF_KEYS: Record<string, string> = {
  'organisation.country': `${REJECTION_REASON_CODES}.conditions.country`,
  'organisation.orgType': `${REJECTION_REASON_CODES}.conditions.orgType`,
  'organisation.activityTags': `${REJECTION_REASON_CODES}.conditions.activityTags`
}

const FORM_ANSWER_REF = /^events\.[^.]+\.formAnswers\./

/**
 * The backend classifies the rejections it can and translates the rest. Prefer the code, so the
 * copy is ours, and fall back to the string it still sends for everything else — including for a
 * code added later, which must never surface as a raw translation key.
 */
export const formatRejectionReason = (
  { rejectionReason, rejectionReasonDetail }: FormatRejectionReasonParams,
  // Narrowed rather than `TFunction`, whose dynamic-key overload widens the return to include
  // null. Same reason `formatValidationRequestFailedMessage` narrows it.
  t: (key: string, options?: Record<string, string>) => string
): string | undefined => {
  const fallback = rejectionReason ?? undefined

  if (!rejectionReasonDetail) return fallback

  switch (rejectionReasonDetail.code) {
    case 'task_failed':
      return t(`${REJECTION_REASON_CODES}.task_failed`, { taskName: rejectionReasonDetail.taskName })

    case 'task_condition_matched':
      return t(`${REJECTION_REASON_CODES}.task_condition_matched`, { taskName: rejectionReasonDetail.taskName })

    case 'check_failed':
      return t(`${REJECTION_REASON_CODES}.check_failed`, {
        check: t(`${REJECTION_REASON_CODES}.checks.${rejectionReasonDetail.check}`)
      })

    /**
     * `name` is shown as authored. The backend advises against it, since the names written so far are
     * identifiers rather than labels, but a partner needs to know which of a program's scheduled
     * events fired and only the name says that. Bounded and normalised before publication.
     */
    case 'scheduled_event':
      return t(`${REJECTION_REASON_CODES}.scheduled_event`, { name: rejectionReasonDetail.name })

    /**
     * The prefix is ours and translated, the text is the admin's and is not — a partner on another
     * locale gets both. Accepted, because the code is the only thing that tells them a person made
     * this decision rather than a rule.
     */
    case 'admin_rejection':
      return rejectionReasonDetail.reason
        ? t(`${REJECTION_REASON_CODES}.admin_rejection`, { reason: rejectionReasonDetail.reason })
        : t(`${REJECTION_REASON_CODES}.admin_rejection_no_reason`)

    case 'condition_matched': {
      const conditions = rejectionReasonDetail.conditions
        .map(ref => CONDITION_REF_KEYS[ref])
        .filter(Boolean)
        .map(key => t(key))

      if (conditions.length > 0) {
        return t(`${REJECTION_REASON_CODES}.condition_matched`, { conditions: conditions.join(', ') })
      }

      return rejectionReasonDetail.conditions.some(ref => FORM_ANSWER_REF.test(ref))
        ? fallback ?? t(`${REJECTION_REASON_CODES}.program_rule`)
        : fallback
    }

    case 'duplicate':
      return t(`${REJECTION_REASON_CODES}.duplicate`)

    default:
      return fallback
  }
}

export const formatDate = (date: string | Date, formatString = 'dd MMM yyyy, HH:mm') => {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  return format(dateObj, formatString)
}

const utf8Decoder = new TextDecoder('utf-8', { fatal: true })

export const decodeDocumentName = (name: string | null) => {
  if (!name) return undefined

  if ([...name].some(char => char.charCodeAt(0) > 0xff)) return name

  try {
    return utf8Decoder.decode(Uint8Array.from(name, char => char.charCodeAt(0)))
  } catch {
    return name
  }
}

export const startOfTodayUtc = () => {
  const date = new Date()
  date.setUTCHours(0, 0, 0, 0)

  return date
}

export const formatUtcDate = (date: string | number | Date, formatString = 'yyyy-MM-dd') =>
  formatInTimeZone(date instanceof Date ? date : new Date(date), 'UTC', formatString)

export type FormatAmountParams = {
  amount?: number
  currency?: string
  locale: string
  t: TFunction
}

export const formatAmount = ({ amount, currency, locale, t }: FormatAmountParams): string => {
  if (amount === undefined || amount === null || !currency) {
    return t('typography.notAvailable')
  }

  return formatMoney(
    {
      amount: Number(amount),
      currency: currency as CurrencyCode
    },
    { locale }
  )
}

export const getCurrentStateBadge = ({
  currentState,
  rejectionReason,
  rejectionReasonDetail,
  t
}: GetCurrentStateBadgeParams): StateBadge => {
  switch (currentState) {
    case CurrentStateLabel.VERIFIED:
      return { label: t('grants.candidateState.verified'), variant: 'default' }
    case CurrentStateLabel.FINALISTS:
      return {
        label: t('grants.candidateState.finalists'),
        variant: 'default'
      }
    case CurrentStateLabel.REJECTED:
      return {
        label: t('grants.candidateState.rejected'),
        variant: 'critical',
        icon: 'reject',
        tooltip: formatRejectionReason({ rejectionReason, rejectionReasonDetail }, t)
      }
    case CurrentStateLabel.DONATION_PAID:
      return {
        label: t('grants.candidateState.donationPaid'),
        variant: 'positive'
      }
    default:
      return { label: currentState, variant: 'default' }
  }
}

type PickerStatusWithLabel = { status: PickerStatus; label: string }

export const mapTaskToAssigneePickerStatus = (task: Task, t: TFunction): PickerStatusWithLabel => {
  if (task.status === 'done') return { status: 'completed', label: t('grants.tasks.status.completed') }

  if (task.status === 'cancelled') return { status: 'cancelled', label: t('grants.tasks.status.cancelled') }

  if (task.status === 'wont_do') return { status: 'wont_do', label: t('grants.tasks.status.wontDo') }

  const isOverdue = task.dueByDate && isAfter(new Date(), parseISO(task.dueByDate))

  if (isOverdue) return { status: 'overdue', label: t('grants.tasks.status.overdue') }

  return { status: 'todo', label: t('grants.tasks.status.todo') }
}

export type PaymentStatus = 'processing' | 'paid'

export type PaymentStatusBadge = {
  label: string
  variant: BadgeVariant
}

export const normalizeWebsite = (value: string) => {
  const trimmed = value.trim()

  if (!trimmed) return trimmed

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

export const capitalizeFirstLetter = (value: string) => {
  const trimmed = value.trim()

  if (!trimmed) return ''

  return trimmed.charAt(0).toLocaleUpperCase() + trimmed.slice(1)
}

export const DEFAULT_LOCALE = 'en-US'

export const getPaymentStatusBadge = (status: PaymentStatus | undefined, t: TFunction): PaymentStatusBadge => {
  switch (status) {
    case 'processing':
      return {
        label: t('grants.disbursements.status.pending'),
        variant: 'default'
      }
    case 'paid':
      return {
        label: t('grants.disbursements.status.paid'),
        variant: 'positive'
      }
    default:
      return {
        label: status ?? t('typography.notAvailable'),
        variant: 'default'
      }
  }
}

export const getDisbursedByLabel = (disbursedBy: DisbursedBy | undefined, t: TFunction): string => {
  switch (disbursedBy) {
    case 'goodstack':
      return t('grants.paymentDetails.disbursedByValue.goodstack')
    case 'other':
      return t('grants.paymentDetails.disbursedByValue.other')
    default:
      return t('typography.notAvailable')
  }
}

export const calculateScoreTone = (ratio: number): 1 | 2 | 3 | 4 | 5 => {
  const clamped = Number.isFinite(ratio) ? Math.min(1, Math.max(0, ratio)) : 0

  if (clamped <= 0.2) return 1

  if (clamped <= 0.4) return 2

  if (clamped <= 0.6) return 3

  if (clamped <= 0.8) return 4

  return 5
}

const DEFAULT_SCORE_WEIGHT = 1

export const calculateWeightedMaxScore = ({ maxScore, scoreWeight }: ScoreScale) =>
  maxScore * (scoreWeight ?? DEFAULT_SCORE_WEIGHT)

export const calculateWeightedTotalMaxScore = (formAnswers: CandidateFormSubmissionAnswer[]) =>
  formAnswers.reduce(
    (total, answer) => (answer.type === 'score' ? total + calculateWeightedMaxScore(answer) : total),
    0
  )

export const MISSING_SCORE_DISPLAY = '-'

export const formatScoreValue = (value: number): number | string => (Number.isInteger(value) ? value : value.toFixed(1))

export const getMatchedScoreDisplay = (
  scores: CandidateAggregateScore[] | undefined,
  scoreName: string | null | undefined
): string | number => {
  if (!scoreName) return MISSING_SCORE_DISPLAY

  const match = scores?.find(s => s.name === scoreName)

  if (match === undefined) return MISSING_SCORE_DISPLAY

  return formatScoreValue(match.value)
}
